import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import { Divider, IconButton, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import React, { useMemo, useState } from "react";
import {
  type DocumentIngestionPipelineFilter,
  useOrderVolumeBySchemaReportQuery,
} from "../../../generated/graphql";
import DateRangePicker, { type DateOption } from "../../date-range-picker";

const NO_SCHEMA_DATA_KEY = "no-schema";

const OrderVolumeBySchemaStatistics = ({
  pipeline,
}: {
  readonly pipeline: DocumentIngestionPipelineFilter;
}) => {
  const [dateOption, setDateOption] = useState<DateOption>({
    startDate: dayjs(new Date()).subtract(30, "days").toDate(),
    endDate: null,
  });
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const { data: orderVolumeBySchemaData, loading } =
    useOrderVolumeBySchemaReportQuery({
      variables: {
        orderVolumeBySchemaInput: {
          startDate: dateOption.startDate,
          endDate: dateOption.endDate,
          pipeline,
        },
      },
    });
  const orderVolumeBySchemaRows = useMemo(
    () =>
      orderVolumeBySchemaData?.orderVolumeBySchemaReport.map((row) => ({
        ...row,
        schemaUuid: row.schemaUuid ?? NO_SCHEMA_DATA_KEY,
      })),
    [orderVolumeBySchemaData],
  );

  const header = (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        border: 1,
        borderBottom: 0,
        borderColor: "#E9ECEF",
        backgroundColor: "#f8f9fa",
        p: 0.5,
      }}
    >
      <Stack>
        <Typography sx={{ fontWeight: "bold" }}>Order volume</Typography>
        <Typography variant="caption">(by schema)</Typography>
      </Stack>
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        spacing={1}
      >
        <DateRangePicker
          dateOption={dateOption}
          onChange={(newDateOption) => {
            setDateOption(newDateOption);
          }}
        />
        <Divider flexItem orientation="vertical" />
        <IconButton
          size="small"
          onClick={() => {
            setIsExpanded((previous) => !previous);
          }}
        >
          {isExpanded ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
        </IconButton>
      </Stack>
    </Stack>
  );

  return (
    <div
      style={
        isExpanded
          ? {
              position: "absolute",
              zIndex: 10_000,
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "white",
            }
          : {}
      }
    >
      {header}
      <DataTable
        key={`open-${isExpanded}`}
        removableSort
        showGridlines
        scrollable
        stripedRows
        value={orderVolumeBySchemaRows}
        sortMode="multiple"
        rows={10}
        loading={loading}
        dataKey="schemaUuid"
        emptyMessage="No data"
        size="small"
        scrollHeight={isExpanded ? "100vh" : "325px"}
        virtualScrollerOptions={{ itemSize: 25 }}
      >
        <Column
          sortable
          filter
          field="schemaName"
          header="Schema"
          filterPlaceholder="Search by schema"
          style={{ width: "80%" }}
        />
        <Column sortable header="Total" field="total" />
      </DataTable>
    </div>
  );
};

export default OrderVolumeBySchemaStatistics;
